import moment from 'moment'
import { filterValidTimeRanges } from '@/utils/appointmentTime'
import {
  DoctorAvailableDate,
  DoctorProperties,
  DoctorFlags,
  DoctorApplication,
  DoctorPolicy,
  DoctorRule,
  RelationDivision
} from '@/infrastructure/network/Monitaro/schema/models'
import HospitalEntity, { IHospitalProps } from '@/entities/Hospital'
import LogEntity, { ILogProps } from '@/entities/Log'

export interface IDoctorProps {
  id?: string
  hospitals?: IHospitalProps[]
  division?: RelationDivision
  occupation?: string | null
  name: string | null
  username: string | null
  login: string | null
  fcmToken: string | null
  uuid: string | null
  fullname: string | null
  kana: string | null
  organization: string | null
  icon: string | null
  invitedAt: string | null
  activateAt: string | null
  createdAt: string | null
  updatedAt: string | null
  log?: ILogProps
  properties: DoctorProperties | null
  flags: DoctorFlags | null
  application: DoctorApplication | null
  policy: DoctorPolicy | null
  role: string | null
  activation: boolean | null
  visitMode?: string | null
  availableDates?: DoctorAvailableDate[] | null
}
export interface IDoctorQueryProps {
  roles: string[] | null
  occupations: string[] | null
  divisions: string[] | null
}

export default class DoctorEntity {
  private _props: IDoctorProps

  constructor(props?: IDoctorProps | null) {
    this._props = EmptyDoctorPropsFactory(props)
  }

  get props(): IDoctorProps {
    return this._props
  }

  get clone(): IDoctorProps {
    return structuredClone(this._props)
  }

  get id(): string | undefined {
    return this._props.id
  }

  get hospitals(): HospitalEntity[] | undefined {
    return this._props.hospitals ? this._props.hospitals.map((prop) => new HospitalEntity(prop)) : []
  }

  get division(): RelationDivision | undefined {
    return this._props.division
  }

  get occupation(): string | null | undefined {
    return this._props.occupation
  }

  get name(): string | null {
    return this._props.name
  }

  get username(): string | null {
    return this._props.username
  }

  get login(): string | null {
    return this._props.login
  }

  get fcmToken(): string | null {
    return this._props.fcmToken
  }

  get uuid(): string | null {
    return this._props.uuid
  }

  get fullname(): string | null {
    return this._props.fullname
  }

  get kana(): string | null {
    return this._props.kana
  }

  get organization(): string | null {
    return this._props.organization
  }

  get icon(): string | null {
    return this._props.icon
  }

  get activateAt(): string | null {
    return this._props.activateAt
  }

  get createdAt(): string | null {
    return this._props.createdAt
  }

  get updatedAt(): string | null {
    return this._props.updatedAt
  }

  get log(): LogEntity | undefined {
    return new LogEntity(this._props.log)
  }

  get properties(): DoctorProperties | null | undefined {
    return this._props.properties
  }

  get flags(): DoctorFlags | null | undefined {
    return this._props.flags
  }

  get application(): DoctorApplication | null | undefined {
    return this._props.application
  }

  get policy(): DoctorPolicy | null | undefined {
    return this._props.policy
  }
}

// 指定日に適用する申請可否のルールを返す。visitMode === 'dates' では面会可能な時間帯が無い日を終日申請不可として扱う
export const resolveDoctorRule = (
  doctor?: Pick<IDoctorProps, 'properties' | 'visitMode' | 'availableDates'> | null,
  date?: string | null
): DoctorRule | null => {
  if (!doctor || !date) return null

  if (doctor.visitMode !== 'dates') {
    const rules = doctor.properties?.rules
    return rules ? rules.find((rule) => rule.day === moment(date).format('ddd')) || null : null
  }

  const day = moment(date).format('ddd')
  const available = Array.isArray(doctor.availableDates)
    ? doctor.availableDates.find((entry) => !!entry && entry.date === moment(date).format('YYYY-MM-DD'))
    : undefined
  // 有効な時間帯（開始 < 終了）が無い日は終日申請不可として扱う。value: 3 で時間帯が空だと、日時選択は営業時間全体を選べる扱いになるため
  const times = filterValidTimeRanges(available && Array.isArray(available.times) ? available.times : [])
  if (!times.length) return { day, value: 2, times: [] }

  return { day, value: 3, times }
}

export const headers = [{ text: '名前', value: 'doctor' }]

export const messages = [
  { text: '', value: 'checked' },
  { text: '名前', value: 'name' },
  { text: '部署', value: 'division' }
]

export const roles = [
  { text: '限定機能ユーザー（医師等）', value: 'user' },
  { text: '全機能ユーザー', value: 'admin' }
]

export const occupations = [
  { text: '医師', value: '医師' },
  { text: '薬剤師', value: '薬剤師' },
  { text: '事務', value: '事務' },
  { text: '看護師', value: '看護師' },
  { text: '技師・士業', value: '技師・士業' },
  { text: '守衛', value: '守衛' },
  { text: 'その他', value: 'その他' }
]

export const EmptyDoctorQueryPropsFactory = (props?: Partial<IDoctorQueryProps> | null): IDoctorQueryProps => ({
  roles: null,
  occupations: null,
  divisions: null,
  ...props
})

export const EmptyDoctorPropsFactory = (props?: Partial<IDoctorProps> | null): IDoctorProps => ({
  name: null,
  username: null,
  login: null,
  fcmToken: null,
  uuid: null,
  fullname: null,
  kana: null,
  organization: null,
  icon: null,
  invitedAt: null,
  activateAt: null,
  createdAt: null,
  updatedAt: null,
  properties: null,
  flags: null,
  application: null,
  policy: null,
  role: null,
  activation: null,
  ...props
})
